import { memo, useCallback, useMemo, useState } from 'react'

import {
  cancelPartnerInactivationRequest,
  fetchPartnerRequestById,
} from 'clickNgo/entities/partner/api'
import { PartnerWorkflowStatus } from 'clickNgo/entities/partner/configs'
import { formatErrorMessages, getJSendErrorMessages } from 'clickNgo/helpers/api'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { useGetFormDataField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { ConfirmDialog } from 'shared/components/confirmDialog'

import { PartnerEditorFormKeys, PartnerEditorFormSettings } from '../../configs'
import { IPartnerEditorData } from '../../models'
import { partnerEditorTranslation } from '../../translation'

import { IPartnerInactivatePanelProps } from './models'

import styles from './PartnerInactivatePanel.module.scss'

export const PartnerInactivePanel = memo<IPartnerInactivatePanelProps>(() => {
  const [isRejectInactivationConfirmDialogVisible, setIsRejectInactivationConfirmDialogVisible] =
    useState(false)
  const [cancellationErrorMessages, setCancellationErrorMessages] = useState<string[]>([])

  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )

  const [, setIsLoading] = useGetFormDataField<boolean>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.IsLoading
  )

  const handleOnInactivationReject = useCallback(() => {
    setIsRejectInactivationConfirmDialogVisible(true)
  }, [])

  const handleOnInactivationRejectConfirm = useCallback(async () => {
    setIsRejectInactivationConfirmDialogVisible(false)
    setCancellationErrorMessages([])
    setIsLoading(true)

    try {
      const { partnerId, locationId } = actualData.partner
      const result = await cancelPartnerInactivationRequest({ partnerId, locationId })
      const errorMessages = getJSendErrorMessages(result)

      if (errorMessages.length) {
        setCancellationErrorMessages(errorMessages)

        return
      }

      const partner = await fetchPartnerRequestById(partnerId, { isForce: true })

      updateActualData({
        ...actualData,
        partner: partner ?? {
          ...actualData.partner,
          status: result.data.partnerStatus,
          workflowStatus: PartnerWorkflowStatus.NONE,
          entryId406: 0,
        },
      })
    } finally {
      setIsLoading(false)
    }
  }, [actualData, setIsLoading, updateActualData])

  const handleOnInactivationRejectReject = useCallback(() => {
    setIsRejectInactivationConfirmDialogVisible(false)
  }, [])

  const hasQueuedInactivation =
    Boolean(actualData.partner.entryId406) &&
    actualData.partner.workflowStatus === PartnerWorkflowStatus.NEW

  const content = useMemo(() => {
    return (
      <div className={styles.inactivatePanel}>
        <p>
          <Translation text={partnerEditorTranslation.inactivePartnerPanel.content} />
        </p>
        {hasQueuedInactivation && (
          <Button color="warning" onClick={handleOnInactivationReject}>
            <Translation text={partnerEditorTranslation.inactivePartnerPanel.button} />
          </Button>
        )}
      </div>
    )
  }, [handleOnInactivationReject, hasQueuedInactivation])

  const formattedCancellationErrorMessages = useMemo(
    () => formatErrorMessages(cancellationErrorMessages),
    [cancellationErrorMessages]
  )

  return (
    <>
      <InfoBlock text={content} type={InfoBlockType.Warning} />
      {cancellationErrorMessages.length > 0 && (
        <InfoBlock
          text={formattedCancellationErrorMessages}
          type={InfoBlockType.Danger}
          hasCloseButton
        />
      )}
      <ConfirmDialog
        title={partnerEditorTranslation.dialog.rejectInactivationPartner.title}
        confirmBtnText={partnerEditorTranslation.dialog.rejectInactivationPartner.confirmBtn}
        rejectBtnText={partnerEditorTranslation.dialog.rejectInactivationPartner.rejectBtn}
        content={partnerEditorTranslation.dialog.rejectInactivationPartner.content}
        isVisible={isRejectInactivationConfirmDialogVisible}
        onConfirm={handleOnInactivationRejectConfirm}
        onReject={handleOnInactivationRejectReject}
      />
    </>
  )
})
